import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Spinner } from "./Spinner"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md border text-sm font-medium tracking-normal transition-colors duration-feedback focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border-primary bg-primary text-primary-foreground hover:border-primary-hover hover:bg-primary-hover active:bg-primary-active",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-hover",
        outline:
          "border-input bg-card text-foreground hover:border-strong hover:bg-muted active:bg-secondary",
        secondary:
          "border-input bg-card text-foreground hover:bg-muted active:bg-secondary",
        ghost: "border-transparent bg-transparent shadow-none hover:bg-muted hover:text-foreground active:bg-secondary",
        link: "border-transparent bg-transparent p-0 text-primary shadow-none underline-offset-4 hover:underline",
        ai: "border-ai bg-ai text-ai-on hover:bg-ai-foreground active:opacity-90",
      },
      size: {
        default: "h-control px-4 py-2",
        sm: "h-control-small rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-5 text-sm",
        icon: "h-control w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, children, disabled, ...props }, ref) => {
    // const Comp = asChild ? Slot : "button"
    const Comp = "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading && (
            <Spinner
                className="mr-2 h-4 w-4"
                aria-label="Loading"
                data-testid="button-spinner"
            />
        )}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
